import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { membershipsAPI } from '../lib/api';
import { CreditCard, Calendar, Building, CheckCircle } from 'lucide-react';

export default function MyDues() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [dues, setDues] = useState([]);

  useEffect(() => {
    const fetchDues = async () => {
      try {
        const res = await membershipsAPI.getMyDues();
        setDues(res.data || []);
      } catch (error) {
        console.error('Error fetching dues:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDues();
  }, []);

  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return null;
    const now = new Date();
    const due = new Date(dueDate);
    const diff = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  if (loading) return <Layout><div className="space-y-4"><Skeleton className="h-8 w-48" />{[1,2].map(i => <Skeleton key={i} className="h-28" />)}</div></Layout>;

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6" data-testid="my-dues-page">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-tight">My Dues</h1>
          <p className="text-sm text-muted-foreground mt-1">Your pending membership fees</p>
        </div>

        {dues.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <CheckCircle className="h-12 w-12 mx-auto mb-3 text-secondary opacity-60" />
              <p className="text-lg font-medium text-secondary">All Clear!</p>
              <p className="text-sm text-muted-foreground mt-1">You have no pending dues</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {dues.map((due) => {
              const daysLeft = getDaysUntilDue(due.due_date);
              const isOverdue = daysLeft !== null && daysLeft < 0;
              return (
                <Card key={due.membership_id} className={isOverdue ? 'border-destructive/50' : ''} data-testid={`due-${due.membership_id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`p-2.5 rounded-lg ${isOverdue ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary'}`}>
                          <CreditCard className="h-5 w-5" />
                        </div>
                        <div>
                          <h3 className="font-heading font-bold text-lg">{due.club_name}</h3>
                          <Badge variant="outline" className="text-xs mt-1">{due.tier_name}</Badge>
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1"><Building className="h-3 w-3" />{due.club_name}</span>
                            {due.due_date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {isOverdue ? (
                                  <span className="text-destructive font-medium">{Math.abs(daysLeft)} {t('membership.daysOverdue')}</span>
                                ) : (
                                  <span>{t('membership.dueIn')} {daysLeft} {t('membership.days')}</span>
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold font-heading">₹{due.amount_due.toLocaleString()}</p>
                        <Badge className={`text-[10px] mt-1 ${isOverdue ? 'bg-destructive' : due.status === 'pending' ? 'bg-amber-500' : 'bg-primary'}`}>
                          {isOverdue ? t('membership.overdue') : due.status}
                        </Badge>
                      </div>
                    </div>
                    <div className="mt-4 flex gap-2">
                      <Link to={`/clubs/${due.club_id}/membership`} className="flex-1">
                        <Button variant="outline" size="sm" className="w-full text-xs">View Membership</Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
